/*
  assets/low-altitude-intake.js — LOW-ALTITUDE-AI-DIAGNOSIS-02

  Canonical runtime owner for /pages/low-altitude-economy.
  Owns the 12-question client-side diagnosis, six-scenario scoring, locked
  recommendation profiles, maturity score, seven-part report preview, local
  draft persistence, result rendering, lead payload preparation and safe
  login/product handoff.

  Note:
  - Preliminary scenario guidance only.
  - No flight permission, airspace approval, compliance approval, subsidy promise,
    investment return promise, checkout mutation, order creation or PDF generation.
  - Shopify owns contact submission, customer login, product, cart and order truth.
*/
(function (root, factory) {
  var api = factory(root || {});
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root) root.LowAltitudeDiagnosis = api;
})(typeof window !== 'undefined' ? window : globalThis, function (window) {
  'use strict';

  var VERSION = 'low-altitude-ai-diagnosis-02';
  var STORAGE_KEY = 'littleg.lowAltitudeDiagnosis.v2';
  var SCENARIO_ORDER = [
    'emergency_fire',
    'park_inspection',
    'medical_logistics',
    'city_governance',
    'cultural_tourism',
    'investment_attraction'
  ];

  var SCENARIOS = {
    emergency_fire: {
      code: 'A',
      zh: '低空應急消防',
      en: 'Low-Altitude Geospatial Emergency Response',
      pilotZh: '先做一個可演示的應急響應流程，整理指揮、現場、航線與責任邊界。',
      pilotEn: 'Start with a demonstrable emergency-response workflow covering command, site, route and responsibility boundaries.'
    },
    park_inspection: {
      code: 'B',
      zh: '園區與設施巡檢',
      en: 'Park and facility inspection',
      pilotZh: '選定一類固定資產或路線，建立巡檢頻率、證據格式與人工覆核流程。',
      pilotEn: 'Select one fixed asset class or route and define inspection frequency, evidence format and human review.'
    },
    medical_logistics: {
      code: 'C',
      zh: '醫療與物資物流',
      en: 'Medical and material logistics',
      pilotZh: '先驗證院區或醫聯體內一條短距離、固定起降點、低頻次的物資路線。',
      pilotEn: 'Validate one short, fixed-endpoint, low-frequency material route within a hospital or medical network.'
    },
    city_governance: {
      code: 'D',
      zh: '低空城市治理',
      en: 'Low-altitude city governance',
      pilotZh: '先把河道、道路、工地或公共設施的一類反覆問題轉成可追蹤的治理任務。',
      pilotEn: 'Turn one recurring river, road, construction or public-facility issue into a trackable governance task.'
    },
    cultural_tourism: {
      code: 'E',
      zh: '文旅展示與城市影像',
      en: 'Tourism showcase and city imagery',
      pilotZh: '先完成一條可公開展示的文旅路線、三維場景或城市影像故事。',
      pilotEn: 'Produce one public-facing tourism route, 3D scene or city-imagery story.'
    },
    investment_attraction: {
      code: 'F',
      zh: '低空產業招商',
      en: 'Low-altitude industry attraction',
      pilotZh: '先把園區資源、可用場景、合作企業與展示節點整理成一頁招商方案。',
      pilotEn: 'Organize park assets, usable scenarios, partner companies and showcase nodes into a one-page attraction brief.'
    }
  };

  var LABELS = {
    unit_type: {
      town_street: ['鎮街', 'Town / district office'],
      park_platform: ['園區平台', 'Park platform'],
      drone_company: ['無人機企業', 'Drone company'],
      fire_emergency: ['消防應急企業', 'Fire / emergency company'],
      medical_logistics: ['醫療物流企業', 'Medical logistics company'],
      planning_institute: ['設計院或規劃院', 'Design / planning institute'],
      tourism: ['景區文旅', 'Tourism / scenic operator'],
      other: ['其他', 'Other']
    },
    primary_problem: {
      emergency: ['應急感知與指揮', 'Geospatial Emergency Response'],
      inspection: ['巡檢', 'Inspection'],
      logistics: ['物流', 'Logistics'],
      investment: ['招商', 'Investment attraction'],
      governance: ['治理', 'Governance'],
      tourism: ['文旅', 'Tourism'],
      presentation: ['匯報展示', 'Briefing / showcase']
    }
  };

  var WEIGHTS = {
    unit_type: {
      town_street: { emergency_fire: 3, city_governance: 4 },
      park_platform: { park_inspection: 3, investment_attraction: 4, city_governance: 1 },
      drone_company: { investment_attraction: 4, park_inspection: 2, emergency_fire: 1 },
      fire_emergency: { emergency_fire: 5, city_governance: 1 },
      medical_logistics: { medical_logistics: 5, emergency_fire: 1 },
      planning_institute: { city_governance: 3, investment_attraction: 2, park_inspection: 1 },
      tourism: { cultural_tourism: 5, investment_attraction: 1 },
      other: { city_governance: 1 }
    },
    primary_problem: {
      emergency: { emergency_fire: 6 },
      inspection: { park_inspection: 6 },
      logistics: { medical_logistics: 6 },
      investment: { investment_attraction: 6 },
      governance: { city_governance: 6 },
      tourism: { cultural_tourism: 6 },
      presentation: { investment_attraction: 3, cultural_tourism: 2, city_governance: 1 }
    },
    drone_team: {
      own_team: { park_inspection: 2, emergency_fire: 2, medical_logistics: 2 },
      partner: { park_inspection: 2, emergency_fire: 2, medical_logistics: 2 },
      negotiating: { investment_attraction: 2, park_inspection: 1 },
      none: { investment_attraction: 1 }
    },
    fixed_site: {
      park: { park_inspection: 4, investment_attraction: 2 },
      hospital: { medical_logistics: 5, emergency_fire: 1 },
      river: { city_governance: 4, park_inspection: 2 },
      scenic: { cultural_tourism: 5 },
      construction: { park_inspection: 5, city_governance: 1 },
      town: { city_governance: 5, emergency_fire: 2 },
      school: { city_governance: 2, medical_logistics: 1, emergency_fire: 1 },
      industrial_park: { investment_attraction: 4, park_inspection: 4 },
      none: { investment_attraction: 1 }
    },
    urgent_goal: {
      leadership_report: { city_governance: 2, investment_attraction: 2 },
      policy_application: { city_governance: 2, emergency_fire: 1, investment_attraction: 1 },
      investment_promotion: { investment_attraction: 5, cultural_tourism: 1 },
      pilot_launch: { park_inspection: 2, emergency_fire: 2, medical_logistics: 2 },
      exhibition: { investment_attraction: 3, cultural_tourism: 3 },
      publicity: { cultural_tourism: 4, investment_attraction: 2 }
    },
    timeline: {
      days_7: { emergency_fire: 2, city_governance: 1 },
      days_30: { emergency_fire: 2, city_governance: 1, investment_attraction: 1 },
      days_90: { park_inspection: 1, medical_logistics: 1, investment_attraction: 1 },
      months_6: { investment_attraction: 2, park_inspection: 1 }
    },
    materials: {
      ppt: { investment_attraction: 1, city_governance: 1 },
      application: { city_governance: 2, emergency_fire: 1 },
      investment_material: { investment_attraction: 3 },
      case: { park_inspection: 1, cultural_tourism: 1 },
      route_plan: { medical_logistics: 2, emergency_fire: 1, park_inspection: 1 }
    },
    risk: {
      airspace: { emergency_fire: 1, city_governance: 1 },
      safety: { emergency_fire: 3 },
      cost: { park_inspection: 1, investment_attraction: 1 },
      unclear_scenario: { city_governance: 1, investment_attraction: 1 },
      supplier_mismatch: { investment_attraction: 2 },
      sustainability: { investment_attraction: 2, city_governance: 1 }
    },
    report_use: {
      internal_report: { city_governance: 2 },
      government_application: { city_governance: 2, emergency_fire: 1 },
      investment_pitch: { investment_attraction: 4 },
      client_visit: { investment_attraction: 2, park_inspection: 1 },
      social_content: { cultural_tourism: 3 },
      exhibition_material: { cultural_tourism: 2, investment_attraction: 2 }
    },
    budget: {
      under_10k: { city_governance: 1, investment_attraction: 1 },
      hkd_10_50k: { park_inspection: 1, city_governance: 1 },
      hkd_50_200k: { park_inspection: 2, medical_logistics: 1, emergency_fire: 1 },
      hkd_200k_1m: { medical_logistics: 2, emergency_fire: 2, investment_attraction: 1 },
      above_1m: { investment_attraction: 2, medical_logistics: 2, emergency_fire: 2 }
    },
    infrastructure: {
      hangar: { park_inspection: 2, emergency_fire: 1 },
      landing_point: { medical_logistics: 3, emergency_fire: 1, park_inspection: 1 },
      park_space: { park_inspection: 2, investment_attraction: 2 },
      data_platform: { city_governance: 3, park_inspection: 1 },
      operator: { medical_logistics: 2, emergency_fire: 2, park_inspection: 1 },
      unclear: {}
    },
    preferred_output: {
      one_page: { investment_attraction: 2, city_governance: 1 },
      ppt: { investment_attraction: 3 },
      scene_image: { cultural_tourism: 3, investment_attraction: 1 },
      pitch_script: { investment_attraction: 4 },
      application_direction: { city_governance: 3, emergency_fire: 1 },
      budget_table: { park_inspection: 2 },
      h5: { cultural_tourism: 3, investment_attraction: 1 }
    }
  };

  var MATURITY = {
    fixed_site: ['park', 'hospital', 'river', 'scenic', 'construction', 'town', 'school', 'industrial_park'],
    drone_team: ['own_team', 'partner'],
    materials: ['ppt', 'application', 'investment_material', 'case', 'route_plan'],
    timeline: ['days_7', 'days_30', 'days_90', 'months_6'],
    budget: ['under_10k', 'hkd_10_50k', 'hkd_50_200k', 'hkd_200k_1m', 'above_1m'],
    infrastructure: ['hangar', 'landing_point', 'park_space', 'data_platform', 'operator']
  };

  var PROFILE_RULES = [
    {
      id: 'town_governance_30',
      match: function (answers) {
        return answers.unit_type === 'town_street' &&
          answers.primary_problem === 'governance' &&
          answers.urgent_goal === 'leadership_report' &&
          answers.timeline === 'days_30';
      },
      top3: ['city_governance', 'emergency_fire', 'park_inspection'],
      zh: {
        recommendations: [
          ['低空城市治理巡查', '以河道、道路、工地或公共設施的一類反覆問題作為首個治理巡查任務。'],
          ['低空應急響應展示', '整理指揮、現場、航線、證據與責任邊界，形成可匯報的應急響應流程。'],
          ['河道 / 道路 / 工地巡檢', '從固定區域和固定頻次開始，建立人工覆核與問題閉環。']
        ],
        services: ['低空城市治理一頁成案服務', '低空匯報 PPT 模板包', '低空試點申報包']
      },
      en: {
        recommendations: [
          ['Low-altitude city-governance patrol', 'Use one recurring river, road, construction or public-facility issue as the first governance patrol task.'],
          ['Emergency-response showcase', 'Organize command, site, route, evidence and responsibility boundaries into a briefing-ready workflow.'],
          ['River / road / construction inspection', 'Start with a fixed area and frequency, with human review and issue closure.']
        ],
        services: ['City-governance one-page plan', 'Low-altitude briefing PPT pack', 'Pilot application package']
      }
    },
    {
      id: 'park_investment_90',
      match: function (answers) {
        return answers.unit_type === 'park_platform' &&
          answers.primary_problem === 'investment' &&
          (answers.urgent_goal === 'exhibition' || answers.urgent_goal === 'investment_promotion') &&
          answers.timeline === 'days_90';
      },
      top3: ['investment_attraction', 'park_inspection', 'emergency_fire'],
      zh: {
        recommendations: [
          ['低空產業園招商展示', '把園區資源、企業能力、可用場景與展示節點整理成招商敘事。'],
          ['園區無人機巡檢', '選定一類園區資產或固定路線，建立巡檢頻率、證據格式與人工覆核。'],
          ['低空測試場 / 機巢示範點', '先做需求、場地、運營與安全條件初判，再交由合資格單位審查。']
        ],
        services: ['低空園區招商手冊模板', '低空招商路演包', '低空產業園一頁方案']
      },
      en: {
        recommendations: [
          ['Low-altitude industry-park showcase', 'Structure park assets, enterprise capabilities, usable scenarios and showcase nodes into an investment narrative.'],
          ['Park drone inspection', 'Select one park asset class or fixed route and define inspection frequency, evidence format and human review.'],
          ['Test field / drone-station demonstration point', 'Complete an initial demand, site, operator and safety review before qualified assessment.']
        ],
        services: ['Industry-park brochure template', 'Low-altitude investment pitch package', 'Industry-park one-page plan']
      }
    },
    {
      id: 'hospital_logistics_90',
      match: function (answers) {
        return answers.fixed_site === 'hospital' &&
          answers.primary_problem === 'logistics' &&
          answers.urgent_goal === 'pilot_launch' &&
          answers.timeline === 'days_90';
      },
      top3: ['medical_logistics', 'emergency_fire', 'park_inspection'],
      zh: {
        recommendations: [
          ['低空醫療物資配送', '先驗證院區內一條短距離、固定起降點、低頻次的醫療物資路線。'],
          ['院區應急物資調度', '整理緊急物資需求、響應流程、責任邊界與備援方案。'],
          ['醫聯體低空物流示範', '以醫聯體內固定節點為起點，先做需求、時效與安全條件初判。']
        ],
        services: ['低空醫療物流試點方案', '低空場景申報書模板包', '低空 AI 診斷 Pro 報告']
      },
      en: {
        recommendations: [
          ['Low-altitude medical-material delivery', 'Validate one short, fixed-endpoint, low-frequency medical-material route within a hospital.'],
          ['Hospital emergency-material dispatch', 'Organize urgent material demand, response workflow, responsibility boundaries and fallback options.'],
          ['Medical-network low-altitude logistics demonstration', 'Start from fixed medical-network nodes and assess demand, timing and safety conditions.']
        ],
        services: ['Medical logistics pilot plan', 'Scenario application template package', 'Low-altitude AI Diagnosis Pro']
      }
    },
    {
      id: 'drone_market_packaging',
      match: function (answers) {
        return answers.unit_type === 'drone_company' &&
          (answers.primary_problem === 'presentation' || answers.primary_problem === 'investment') &&
          answers.urgent_goal === 'investment_promotion';
      },
      top3: ['investment_attraction', 'park_inspection', 'city_governance'],
      zh: {
        recommendations: [
          ['產品場景化包裝', '把設備、平台或能力改寫成客戶可理解的場景、流程、交付與價值。'],
          ['低空應用案例庫', '按 Demo、已驗證案例與可探索方向分級整理，避免混淆成熟度。'],
          ['政府 / 園區客戶路演方案', '把政策背景、場景價值、試點路線、預算與合作邊界整理成路演結構。']
        ],
        services: ['低空招商路演包', '低空場景案例包', '低空企業產品包裝服務']
      },
      en: {
        recommendations: [
          ['Scenario-based product packaging', 'Translate equipment, platform or capability into customer-facing scenarios, workflows, deliverables and value.'],
          ['Low-altitude application case library', 'Separate demos, verified cases and exploratory directions to avoid overstating maturity.'],
          ['Government / park customer pitch plan', 'Structure policy context, scenario value, pilot route, budget and collaboration boundaries into a pitch.']
        ],
        services: ['Low-altitude investment pitch package', 'Scenario case package', 'Enterprise product-packaging service']
      }
    }
  ];

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function isChinese(locale) {
    return clean(locale).toLowerCase().indexOf('zh') === 0;
  }

  function label(group, value, zh) {
    var item = LABELS[group] && LABELS[group][clean(value)];
    return item ? item[zh ? 0 : 1] : clean(value || (zh ? '未提供' : 'Not provided'));
  }

  function addWeights(scores, weights) {
    Object.keys(weights || {}).forEach(function (key) {
      if (Object.prototype.hasOwnProperty.call(scores, key)) scores[key] += Number(weights[key]) || 0;
    });
  }

  function scoreAnswers(answers) {
    answers = answers || {};
    var scores = {};
    SCENARIO_ORDER.forEach(function (key) { scores[key] = 0; });
    Object.keys(WEIGHTS).forEach(function (field) {
      var value = clean(answers[field]);
      addWeights(scores, WEIGHTS[field][value] || {});
    });
    var ranked = SCENARIO_ORDER.slice().sort(function (left, right) {
      var delta = scores[right] - scores[left];
      return delta || SCENARIO_ORDER.indexOf(left) - SCENARIO_ORDER.indexOf(right);
    });
    return { scores: scores, ranked: ranked, top3: ranked.slice(0, 3) };
  }

  function calculateMaturity(answers) {
    answers = answers || {};
    var score = 0;
    if (MATURITY.fixed_site.indexOf(clean(answers.fixed_site)) >= 0) score += 20;
    if (MATURITY.drone_team.indexOf(clean(answers.drone_team)) >= 0) score += 20;
    if (MATURITY.materials.indexOf(clean(answers.materials)) >= 0) score += 15;
    if (MATURITY.timeline.indexOf(clean(answers.timeline)) >= 0) score += 15;
    if (MATURITY.budget.indexOf(clean(answers.budget)) >= 0) score += 15;
    if (MATURITY.infrastructure.indexOf(clean(answers.infrastructure)) >= 0) score += 15;

    var level;
    if (score <= 35) {
      level = {
        code: 'L1',
        zh: '概念期',
        en: 'Concept stage',
        conclusionZh: '先做匯報材料和場景診斷。',
        conclusionEn: 'Start with briefing materials and scenario diagnosis.'
      };
    } else if (score <= 65) {
      level = {
        code: 'L2',
        zh: '試點準備期',
        en: 'Pilot preparation',
        conclusionZh: '可做 1–3 個小場景試點。',
        conclusionEn: 'Prepare one to three small pilot scenarios.'
      };
    } else if (score <= 85) {
      level = {
        code: 'L3',
        zh: '示範項目期',
        en: 'Demonstration project',
        conclusionZh: '可做申報包、招商包或展示包。',
        conclusionEn: 'Progress to an application, investment or showcase package.'
      };
    } else {
      level = {
        code: 'L4',
        zh: '平台擴展期',
        en: 'Platform expansion',
        conclusionZh: '可規劃低空場景矩陣或園區低空方案。',
        conclusionEn: 'Plan a scenario matrix or park-level low-altitude programme.'
      };
    }
    return { score: score, level: level };
  }

  function matchProfile(answers) {
    for (var i = 0; i < PROFILE_RULES.length; i += 1) {
      if (PROFILE_RULES[i].match(answers || {})) return PROFILE_RULES[i];
    }
    return null;
  }

  function budgetRecommendation(value, zh) {
    var map = {
      under_10k: ['模板級 / 診斷級', 'Template / diagnosis tier'],
      hkd_10_50k: ['診斷級 / PPT 或申報方向', 'Diagnosis / PPT or application direction'],
      hkd_50_200k: ['顧問級 / 一頁成案或試點包', 'Advisory / one-page plan or pilot package'],
      hkd_200k_1m: ['試點級 / 跨單位驗證與展示', 'Pilot / multi-party validation and showcase'],
      above_1m: ['平台級 / 場景矩陣與持續運營', 'Platform / scenario matrix and ongoing operations'],
      uncertain: ['先用 Basic 報告校準預算', 'Use the Basic report to calibrate budget']
    };
    var item = map[clean(value)] || map.uncertain;
    return item[zh ? 0 : 1];
  }

  function genericServices(answers, top3, zh) {
    var list = [];
    var output = clean(answers.preferred_output);
    var primary = top3[0];
    if (output === 'ppt' || output === 'pitch_script' || clean(answers.urgent_goal) === 'investment_promotion') {
      list.push(zh ? '低空招商路演包' : 'Low-altitude investment pitch package');
    }
    if (output === 'application_direction' || clean(answers.urgent_goal) === 'policy_application') {
      list.push(zh ? '低空試點申報包' : 'Low-altitude pilot application package');
    }
    if (primary === 'medical_logistics') list.push(zh ? '低空醫療物流試點方案' : 'Medical logistics pilot plan');
    else if (primary === 'investment_attraction') list.push(zh ? '低空產業園一頁方案' : 'Low-altitude industry-park one-page plan');
    else if (primary === 'city_governance') list.push(zh ? '低空城市治理一頁成案服務' : 'Low-altitude city-governance one-page plan');
    else list.push(zh ? '低空場景一頁成案服務' : 'Low-altitude scenario one-page plan');
    list.push(zh ? '低空 AI 診斷報告 Basic' : 'Low-altitude AI Diagnosis Basic');
    return list.filter(function (item, index, all) { return all.indexOf(item) === index; }).slice(0, 3);
  }

  function genericRecommendations(scored, zh) {
    return scored.top3.map(function (key) {
      var scenario = SCENARIOS[key];
      return {
        key: key,
        label: zh ? scenario.zh : scenario.en,
        summary: zh ? scenario.pilotZh : scenario.pilotEn,
        score: scored.scores[key] || 0
      };
    });
  }

  function profileRecommendations(profile, scores, zh) {
    var source = profile[zh ? 'zh' : 'en'].recommendations;
    return source.map(function (item, index) {
      var key = profile.top3[index];
      return {
        key: key,
        label: item[0],
        summary: item[1],
        score: scores[key] || 0
      };
    });
  }

  function filingDirections(top3, answers, zh) {
    var directions = [];
    function add(zhText, enText) {
      var value = zh ? zhText : enText;
      if (directions.indexOf(value) < 0) directions.push(value);
    }
    top3.forEach(function (key) {
      if (key === 'city_governance') add('智慧治理 / 公共服務示範場景方向', 'Smart governance / public-service demonstration direction');
      if (key === 'emergency_fire') add('應急管理 / 消防救援場景需求清單', 'Emergency-management / fire-rescue scenario requirements');
      if (key === 'park_inspection') add('行業巡檢 / 園區基礎設施協同方向', 'Industry inspection / park-infrastructure coordination direction');
      if (key === 'medical_logistics') add('醫療物資 / 公共服務試點方向', 'Medical-material / public-service pilot direction');
      if (key === 'cultural_tourism') add('文旅展示 / 城市影像應用方向', 'Tourism showcase / city-imagery application direction');
      if (key === 'investment_attraction') add('低空產業平台 / 園區招商展示方向', 'Low-altitude industry platform / park investment-showcase direction');
    });
    if (answers.urgent_goal === 'policy_application' || answers.report_use === 'government_application') {
      add('示範場景申報材料準備方向', 'Demonstration-scenario filing-material preparation direction');
    }
    return directions.slice(0, 3);
  }

  function routePlan(zh) {
    return {
      day30: zh
        ? '整理場景、責任、材料待完善與匯報摘要。'
        : 'Organize scenario, responsibility, material gaps and briefing summary.',
      day60: zh
        ? '確認試點範圍、合作角色、預算框架與安全問題。'
        : 'Confirm pilot scope, partner roles, budget framework and safety questions.',
      day90: zh
        ? '形成展示、申報或招商版本，交由合資格單位審查。'
        : 'Prepare a showcase, filing or investment version for qualified review.'
    };
  }

  function buildReportModules(result, answers, zh) {
    var topLabels = result.recommendations.map(function (item) { return item.label; }).join(' / ');
    var filing = result.filing_directions.join(' / ');
    return [
      { code: '01', title: zh ? '診斷摘要' : 'Diagnosis summary', content: result.diagnosis_summary },
      { code: '02', title: zh ? '推薦場景 Top 3' : 'Top 3 scenarios', content: topLabels },
      { code: '03', title: zh ? '首個試點建議' : 'First pilot recommendation', content: result.first_pilot },
      { code: '04', title: zh ? '30 / 60 / 90 天路線' : '30 / 60 / 90-day route', content: [result.route.day30, result.route.day60, result.route.day90].join(' → ') },
      { code: '05', title: zh ? '初步預算區間' : 'Initial budget band', content: result.budget_recommendation },
      { code: '06', title: zh ? '可申報方向' : 'Possible filing directions', content: filing },
      { code: '07', title: zh ? '下一步服務建議' : 'Next service recommendations', content: result.services.join(' / ') }
    ];
  }

  function buildResult(answers, locale) {
    answers = answers || {};
    var zh = isChinese(locale);
    var scored = scoreAnswers(answers);
    var maturity = calculateMaturity(answers);
    var profile = matchProfile(answers);
    var top3 = profile ? profile.top3.slice() : scored.top3.slice();
    var recommendations = profile
      ? profileRecommendations(profile, scored.scores, zh)
      : genericRecommendations({ top3: top3, scores: scored.scores }, zh);
    var services = profile
      ? profile[zh ? 'zh' : 'en'].services.slice()
      : genericServices(answers, top3, zh);
    var route = routePlan(zh);
    var firstScenario = SCENARIOS[top3[0]];
    var result = {
      schema_version: 'low_altitude_diagnosis_v2',
      source: 'low-altitude-economy',
      status: 'preliminary_only',
      created_at: new Date().toISOString(),
      profile_id: profile ? profile.id : 'general',
      answers: Object.assign({}, answers),
      scores: scored.scores,
      top3: top3,
      recommendations: recommendations,
      maturity_score: maturity.score,
      maturity_level: maturity.level.code,
      maturity_label: zh ? maturity.level.zh : maturity.level.en,
      maturity_conclusion: zh ? maturity.level.conclusionZh : maturity.level.conclusionEn,
      diagnosis_summary: zh
        ? '單位類型：' + label('unit_type', answers.unit_type, true) + '；核心需求：' + label('primary_problem', answers.primary_problem, true) + '；成熟度：' + maturity.level.code + ' ' + maturity.level.zh + '。'
        : 'Organization: ' + label('unit_type', answers.unit_type, false) + '; primary need: ' + label('primary_problem', answers.primary_problem, false) + '; maturity: ' + maturity.level.code + ' ' + maturity.level.en + '.',
      first_pilot: recommendations[0] ? recommendations[0].summary : (zh ? firstScenario.pilotZh : firstScenario.pilotEn),
      route: route,
      budget_recommendation: budgetRecommendation(answers.budget, zh),
      filing_directions: filingDirections(top3, answers, zh),
      services: services,
      disclaimer: zh
        ? '此診斷為低空場景初步研判，不構成飛行許可、合規審批、正式申報文件或投資承諾。'
        : 'This assessment provides preliminary scenario guidance only—it is not a flight permit, regulatory approval, or investment commitment.'
    };
    result.report_modules = buildReportModules(result, answers, zh);
    return result;
  }

  function safeStore(result) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(result));
      return true;
    } catch (_) {
      return false;
    }
  }

  function readStored() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }

  function all(root, selector) {
    return Array.prototype.slice.call((root || window.document).querySelectorAll(selector));
  }

  function setText(root, selector, value) {
    var node = root.querySelector(selector);
    if (node) node.textContent = clean(value);
  }

  function renderList(root, selector, items) {
    var node = root.querySelector(selector);
    if (!node) return;
    node.innerHTML = '';
    (items || []).forEach(function (item) {
      var li = window.document.createElement('li');
      li.textContent = clean(item);
      node.appendChild(li);
    });
  }

  function renderReportModules(root, modules) {
    var node = root.querySelector('[data-la-report-outline]');
    if (!node) return;
    node.innerHTML = '';
    (modules || []).forEach(function (module) {
      var li = window.document.createElement('li');
      var strong = window.document.createElement('strong');
      strong.textContent = module.code + ' · ' + module.title;
      var span = window.document.createElement('span');
      span.textContent = module.content;
      li.appendChild(strong);
      li.appendChild(span);
      node.appendChild(li);
    });
  }

  function renderResult(root, result, options) {
    var panel = root.querySelector('[data-la-result]');
    if (!panel || !result || !Array.isArray(result.recommendations)) return;

    all(panel, '[data-la-result-scenario]').forEach(function (node, index) {
      var item = result.recommendations[index];
      if (!item) return;
      node.dataset.scenario = item.key;
      setText(node, '[data-la-result-rank]', String(index + 1).padStart(2, '0'));
      setText(node, '[data-la-result-title]', item.label);
      setText(node, '[data-la-result-summary]', item.summary);
      setText(node, '[data-la-result-score]', String(item.score || 0));
    });

    setText(panel, '[data-la-diagnosis-summary]', result.diagnosis_summary);
    setText(panel, '[data-la-maturity-score]', String(result.maturity_score));
    setText(panel, '[data-la-maturity-level]', result.maturity_level + ' · ' + result.maturity_label);
    setText(panel, '[data-la-maturity-conclusion]', result.maturity_conclusion);
    setText(panel, '[data-la-first-pilot]', result.first_pilot);
    setText(panel, '[data-la-budget]', result.budget_recommendation);
    setText(panel, '[data-la-route-30]', result.route.day30);
    setText(panel, '[data-la-route-60]', result.route.day60);
    setText(panel, '[data-la-route-90]', result.route.day90);
    renderList(panel, '[data-la-filing-directions]', result.filing_directions);
    renderList(panel, '[data-la-services]', result.services);
    renderReportModules(panel, result.report_modules);

    var body = root.querySelector('[data-la-lead-body]');
    if (body) {
      body.value = JSON.stringify({
        schema_version: result.schema_version,
        source: result.source,
        created_at: result.created_at,
        profile_id: result.profile_id,
        diagnosis_summary: result.diagnosis_summary,
        maturity_score: result.maturity_score,
        maturity_level: result.maturity_level,
        recommendations: result.recommendations,
        budget_recommendation: result.budget_recommendation,
        filing_directions: result.filing_directions,
        services: result.services,
        report_modules: result.report_modules,
        answers: result.answers,
        disclaimer: result.disclaimer
      }, null, 2);
    }

    root.dataset.laStatus = 'result-ready';
    panel.hidden = false;
    panel.setAttribute('tabindex', '-1');

    if (!options || options.quiet !== true) {
      const disclosure = panel.closest('details.la-tools');
      if (disclosure) disclosure.open = true;

      let reduceMotion = false;
      try {
        reduceMotion = window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches;
      } catch (_) {}

      try { panel.focus({ preventScroll: true }); } catch (_) {}
      try {
        panel.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'start'
        });
      } catch (_) {}
    }
    try {
      window.dispatchEvent(new CustomEvent('low-altitude:diagnosis-ready', { detail: result }));
    } catch (_) {}
  }

  function collectAnswers(form) {
    var data = new FormData(form);
    var answers = {};
    all(form, '[data-la-question]').forEach(function (field) {
      var name = clean(field.getAttribute('data-la-question'));
      answers[name] = clean(data.get(name));
    });
    return answers;
  }

  function validateStep(step) {
    var required = all(step, 'input[required], select[required], textarea[required]');
    for (var i = 0; i < required.length; i += 1) {
      if (!required[i].checkValidity()) {
        required[i].reportValidity();
        return false;
      }
    }
    return true;
  }

   const workloadEstimates = new WeakMap();

  function workloadFingerprint(panel) {
    return JSON.stringify({
      services: Array.from(
        panel.querySelectorAll('[data-la-service]:checked'),
        (input) => input.value
      ),
      inputs: Array.from(
        panel.querySelectorAll('[data-la-workload]'),
        (input) => [input.dataset.laWorkload, input.value]
      )
    });
  }

  function readWorkloadEstimate(panel) {
    const entry = workloadEstimates.get(panel);
    const notes = panel.querySelector('[data-la-project-notes]');

    if (
      !entry ||
      !notes ||
      entry.fingerprint !== workloadFingerprint(panel) ||
      !notes.value.includes(entry.block)
    ) return null;

    return entry.estimate;
  }

  function mountWorkloadEstimator(root) {
    const panel = root.querySelector('[data-la-project-preparation]');
    if (!panel || panel.dataset.laWorkloadBound === 'true') return;

    const dialog = panel.querySelector('[data-la-workload-dialog]');
    const open = panel.querySelector('[data-la-workload-open]');
    const notes = panel.querySelector('[data-la-project-notes]');
    const status = panel.querySelector('[data-la-handoff-status]');
    if (!dialog || !open || !notes || !status) return;

    if (typeof dialog.showModal !== 'function') {
      open.title = isChinese(root.dataset.locale)
        ? '此浏览器不支持结果弹窗。'
        : 'This browser does not support the results dialog.';
      return;
    }

    panel.dataset.laWorkloadBound = 'true';
    const zh = isChinese(root.dataset.locale);
    const words = (cn, en) => zh ? cn : en;
    const format = (value) => new Intl.NumberFormat(
      zh ? 'zh-HK' : 'en',
      { maximumFractionDigits: 2 }
    ).format(value);
    const result = dialog.querySelector('[data-la-workload-result]');
    const basis = dialog.querySelector('[data-la-workload-basis]');
    const feedback = dialog.querySelector('[data-la-workload-feedback]');
    let candidate = null;

    function input(name) {
      const node = panel.querySelector(
        `[data-la-workload="${name}"]`
      );
      if (
        !node ||
        node.value.trim() === '' ||
        !node.checkValidity() ||
        !Number.isFinite(node.valueAsNumber)
      ) {
        if (node) {
          let disclosure = node.closest('details');
          while (disclosure) {
            disclosure.open = true;
            disclosure = disclosure.parentElement
              ? disclosure.parentElement.closest('details')
              : null;
          }
          node.focus();
          node.reportValidity();
        }
        throw new Error('invalid_input');
      }
      return node.valueAsNumber;
    }

    function selected(service) {
      const node = panel.querySelector(
        `[data-la-service][value="${service}"]`
      );
      return Boolean(node && node.checked);
    }

    open.addEventListener('click', () => {
      status.textContent = '';
      candidate = null;

      if (notes.readOnly) {
        status.textContent = words(
          '已有专业草稿，请进入草稿页修改。本页不会追加估算。',
          'Edit the saved professional brief. No estimate was added here.'
        );
        return;
      }

      const inspection = selected('mission-management');
      const review = selected('governance-events');
      if (!inspection && !review) {
        status.textContent = words(
          '请先在上方选择“任务空间管理”或“治理事件数据”，再填写对应工时。三维数据服务暂不提供此估算。',
          'Select Operations Management or Governance Events above, then enter the relevant hours. This estimate is not available for 3D data services.'
        );
        return;
      }

      const tasks = [];
      const calculations = [];
      try {
        if (inspection) {
          const points = input('points');
          const frequency = input('frequency');
          const minutes = input('inspection_minutes');
          const available = input('inspection_hours');
          const visits = points * frequency;
          const required = visits * minutes / 60;
          tasks.push({
            task: 'inspection',
            inputs: { points, frequency, minutes, available_hours: available },
            visits_per_month: visits,
            required_hours: required,
            available_hours: available,
            gap_hours: Math.max(0, required - available)
          });
          calculations.push(
            `${points} × ${frequency} × ${minutes} ÷ 60 = ${format(required)} h`
          );
        }

        if (review) {
          const events = input('events');
          const minutes = input('review_minutes');
          const available = input('review_hours');
          const required = events * minutes / 60;
          tasks.push({
            task: 'event_review',
            inputs: { events, minutes, available_hours: available },
            required_hours: required,
            available_hours: available,
            gap_hours: Math.max(0, required - available)
          });
          calculations.push(
            `${events} × ${minutes} ÷ 60 = ${format(required)} h`
          );
        }
      } catch (_) {
        status.textContent = words(
          '请补齐已选任务的有效数值，可投入工时未知时不要填0代替。',
          'Complete valid inputs for selected tasks. Do not use zero to represent unknown available hours.'
        );
        return;
      }

      const lines = tasks.map((task) => {
        const title = task.task === 'inspection'
          ? words('巡检', 'Inspection')
          : words('事件复核', 'Event review');
        return words(
          `${title}：需求${format(task.required_hours)}h/月；可投入${format(task.available_hours)}h/月；待完善${format(task.gap_hours)}h/月`,
          `${title}: ${format(task.required_hours)} required h/month; ${format(task.available_hours)} available h/month; ${format(task.gap_hours)} gap h/month`
        );
      });

      const summary = [
        words('工作量与产能自评，非报价', 'Workload and capacity self-assessment, not a quotation'),
        ...lines,
        words('计算：', 'Calculations: ') + calculations.join(' / '),
        words('不合计不同任务；未计交通、准备、延误及返工。', 'Tasks are not combined; travel, preparation, delays and rework are excluded.')
      ].join('\n');

      candidate = {
        fingerprint: workloadFingerprint(panel),
        estimate: {
          version: 1,
          formula_version: 'monthly-task-hours-v1',
          period: 'monthly',
          basis: 'user_inputs',
          verified: false,
          created_at: Date.now(),
          tasks,
          exclusions: [
            'travel', 'preparation', 'weather_delays', 'rework',
            'exceptions', 'permit_related_work'
          ]
        },
        block: `[LA_CAPACITY_V1]\n${summary}\n[/LA_CAPACITY_V1]`
      };

      result.textContent = lines.join('\n');
      basis.textContent = calculations.join('\n');
      feedback.textContent = '';
      if (!dialog.open) dialog.showModal();
      dialog.querySelector('[data-la-workload-title]').focus();
    });

    dialog.querySelector('[data-la-workload-close]').addEventListener(
      'click', () => dialog.close()
    );

    dialog.addEventListener('close', () => {
      if (open.isConnected) open.focus({ preventScroll: true });
    });

    dialog.querySelector('[data-la-workload-apply]').addEventListener(
      'click', () => {
        if (
          !candidate ||
          notes.readOnly ||
          candidate.fingerprint !== workloadFingerprint(panel)
        ) {
          feedback.textContent = words(
            '输入或服务选择已变化，请重新计算；未带入结果。',
            'Inputs or services changed. Calculate again; no result was applied.'
          );
          return;
        }

        const pattern = /\[LA_CAPACITY_V1\][\s\S]*?\[\/LA_CAPACITY_V1\]/g;
        const current = notes.value;
        const blocks = current.match(pattern) || [];
        const remainder = current.replace(pattern, '');

        if (
          blocks.length > 1 ||
          remainder.includes('[LA_CAPACITY_V1]') ||
          remainder.includes('[/LA_CAPACITY_V1]')
        ) {
          feedback.textContent = words(
            '旧估算标记不完整或重复，请先整理文本。未覆盖原资料。',
            'Existing estimate markers are incomplete or duplicated. Edit the notes first; nothing was overwritten.'
          );
          return;
        }

        const next = blocks.length
          ? current.replace(blocks[0], candidate.block)
          : [current.trim(), candidate.block].filter(Boolean).join('\n\n');

        if (notes.maxLength > 0 && next.length > notes.maxLength) {
          feedback.textContent = words(
            `带入后超过${notes.maxLength}字符，请先精简原文。本次未截断。`,
            `The result exceeds ${notes.maxLength} characters. Shorten the notes first; nothing was truncated.`
          );
          return;
        }

        notes.value = next;
        notes.dispatchEvent(new window.Event('input', { bubbles: true }));
        workloadEstimates.set(panel, candidate);
        status.textContent = words(
          '结果已带入本页需求资料，尚未保存或发送；再次应用会替换旧估算。',
          'Results applied to request details, not saved or sent. Applying again replaces the previous estimate.'
        );
        dialog.close();
      }
    );

    open.disabled = false;
  }

  function readCapabilityAssessment(panel, services) {
    const allowed = ['unknown', 'self', 'partner', 'self_partner'];

    return services.map((service) => {
      const input = Array.from(
        panel.querySelectorAll('[data-la-capability]')
      ).find((node) => node.getAttribute('data-la-capability') === service);

      const state = input && allowed.includes(input.value)
        ? input.value
        : 'unknown';

      return {
        capability: service,
        state,
        basis: state === 'unknown'
          ? 'not_confirmed'
          : 'user_self_report',
        verified: false
      };
    });
  }

  function mountProjectPreparation(root) {
    const panel = root.querySelector('[data-la-project-preparation]');
    if (!panel || panel.dataset.laPreparationBound === 'true') return;

    const button = panel.querySelector('[data-la-continue-brief]');
    const location = panel.querySelector('[data-la-project-location]');
    const notes = panel.querySelector('[data-la-project-notes]');
    const context = panel.querySelector('[data-la-tender-context]');
    const status = panel.querySelector('[data-la-handoff-status]');
    if (!button || !location || !notes || !context || !status) return;

    panel.dataset.laPreparationBound = 'true';

    const key = 'gcp_dashboard_brief_handoff_v1';
    const zh = isChinese(root.dataset.locale);
    const incomingSource = new URLSearchParams(window.location.search).get('source');
    const labels = zh
      ? {
          'spatial-data': '低空三维空间数据',
          'mission-management': '低空任务空间管理',
          'governance-events': '城市治理事件数据'
        }
      : {
          'spatial-data': 'Low-altitude 3D spatial data',
          'mission-management': 'Operations Management',
          'governance-events': 'Governance Events'
        };

    function readHandoff() {
      const raw = window.sessionStorage.getItem(key);
      if (!raw) return null;
      const value = JSON.parse(raw);
      const age = Date.now() - Number(value && value.created_at);
      if (
        !value ||
        value.version !== 1 ||
        !Number.isFinite(age) ||
        age < 0 ||
        age > 30 * 60 * 1000
      ) throw new Error('invalid_handoff');
      return value;
    }

    function accepts(value) {
      if (!value) return false;

      if (incomingSource === 'gtender') {
        const expectedTender = new URLSearchParams(
          window.location.search
        ).get('tender');

        return Boolean(
          value.source === 'gtender' &&
          expectedTender &&
          /^[A-Za-z0-9._:-]{3,240}$/.test(expectedTender) &&
          value.tender_context &&
          value.tender_context.id === expectedTender
        );
      }

      return value.source === 'low-altitude-services';
    }

    try {
      const value = readHandoff();
      if (accepts(value)) {
        location.value = String(value.project_location || '').slice(0, 240);
        const preparation = value.low_altitude_preparation;
        if (preparation) {
          notes.value = String(preparation.notes || '').slice(0, 600);
          const selected = Array.isArray(preparation.services)
            ? preparation.services
            : [];
          panel.querySelectorAll('[data-la-service]').forEach((input) => {
            input.checked = selected.includes(input.value);
          });
        }
        if (value.brief_record_id) {
          if (!/^[A-Za-z0-9._:-]{1,160}$/.test(value.brief_record_id)) {
            throw new Error('invalid_brief_record');
          }

          location.readOnly = true;
          notes.readOnly = true;
          panel.querySelectorAll('[data-la-service]').forEach((input) => {
            input.disabled = true;
          });

          button.textContent = zh
            ? '继续编辑已有专业需求草稿'
            : 'Continue editing the saved professional brief';

          status.textContent = zh
            ? '这份需求已保存为专业草稿。本页保留原准备内容，完整需求及后续修改请在专业需求页查看和更新。'
            : 'A professional draft has been saved. This page retains the preparation details; view and update the full request in the professional brief.';
        }

        const assessments = Array.isArray(value.capability_assessments)
          ? value.capability_assessments
          : [];

        panel.querySelectorAll('[data-la-capability]').forEach((input) => {
          const row = assessments.find((item) =>
            item &&
            item.capability === input.getAttribute('data-la-capability') &&
            ['unknown', 'self', 'partner', 'self_partner'].includes(item.state)
          );

          input.value = row ? row.state : 'unknown';
          input.disabled = Boolean(value.brief_record_id);
        });

        const tender = value.tender_context;
        if (tender && typeof tender.title === 'string') {
          context.textContent = (zh ? '当前招标：' : 'Selected tender: ') +
            tender.title.slice(0, 500);
          context.hidden = false;
        }
      } else if (incomingSource === 'gtender') {
        status.textContent = zh
          ? '未找到本次招标交接资料，请返回招标详情重新进入。'
          : 'Tender context is missing. Return to the tender detail and enter again.';
      }
    } catch (_) {
      status.textContent = zh
        ? '暂存资料不可用或已过期。未清除原资料，请返回原入口重新开始。'
        : 'Temporary details are unavailable or expired. Existing data was not cleared.';
    }

    button.addEventListener('click', () => {
      status.textContent = '';
      const services = Array.from(
        panel.querySelectorAll('[data-la-service]:checked'),
        (input) => input.value
      ).filter((value) => Object.prototype.hasOwnProperty.call(labels, value));

      if (!services.length) {
        status.textContent = zh
          ? '请至少选择一项服务。'
          : 'Select at least one service.';
        return;
      }

      try {
        const stored = readHandoff();
        if (stored && !accepts(stored)) {
          throw new Error('other_handoff');
        }

        const hasEstimateText =
          notes.value.includes('[LA_CAPACITY_V1]') ||
          notes.value.includes('[/LA_CAPACITY_V1]');

        if (
          !(stored && stored.brief_record_id) &&
          hasEstimateText &&
          !readWorkloadEstimate(panel)
        ) {
          status.textContent = zh
            ? '估算摘要与当前输入未能核对。请重新计算并应用，或移除完整估算块后继续。原文未清除，也未跳转。'
            : 'The estimate summary could not be matched to current inputs. Recalculate and apply it, or remove the complete estimate block before continuing. Nothing was cleared and navigation was stopped.';
          return;
        }
        if (
          incomingSource === 'gtender' &&
          (!stored || stored.source !== 'gtender' || !stored.tender_context)
        ) {
          throw new Error('missing_tender_context');
        }

        const destination = new URL(
          panel.getAttribute('data-la-brief-url'),
          window.location.origin
        );
        if (
          destination.origin !== window.location.origin ||
          destination.searchParams.get('intent') !== 'professional-review'
        ) throw new Error('invalid_destination');

        const source = stored ? stored.source : 'low-altitude-services';

        if (stored && stored.brief_record_id) {
          if (!/^[A-Za-z0-9._:-]{1,160}$/.test(stored.brief_record_id)) {
            throw new Error('invalid_brief_record');
          }

          const previous = stored.low_altitude_preparation;
          const unchanged =
            previous &&
            Array.isArray(previous.services) &&
            JSON.stringify(previous.services) === JSON.stringify(services) &&
            String(previous.notes || '') === notes.value.trim() &&
            String(stored.project_location || '') === location.value.trim();

          if (!unchanged) {
            status.textContent = zh
              ? '已有保存的专业需求草稿。请恢复本页原值后继续，在专业需求页修改，避免覆盖已保存内容。'
              : 'A professional brief has already been saved. Restore the preparation values, then edit in the brief to avoid overwriting saved content.';
            return;
          }

          destination.searchParams.set('source', source);
          destination.searchParams.set('record_id', stored.brief_record_id);
          destination.searchParams.delete('result_id');
          destination.searchParams.delete('dashboard_handoff');
          window.location.assign(destination.href);
          return;
        }
        const base = stored && typeof stored.low_altitude_base_description === 'string'
          ? stored.low_altitude_base_description
          : String(stored && stored.description || '');

        const additions = [
          zh ? '低空服务准备' : 'Low-altitude service preparation',
          (zh ? '所选服务：' : 'Services: ') +
            services.map((value) => labels[value]).join(' / ')
        ];
        const capabilityAssessments = readCapabilityAssessment(
          panel, services
        );
        const assessmentLabels = zh
          ? {
              unknown: '待确认',
              self: '本方自评可覆盖，未经核验',
              partner: '本方确认需要伙伴补充，范围待确认',
              self_partner: '本方自评可覆盖，但希望合作'
            }
          : {
              unknown: 'to confirm',
              self: 'self-reported coverage, unverified',
              partner: 'partner support requested; scope to confirm',
              self_partner: 'self-reported coverage; collaboration requested'
            };

        additions.push(
          (zh ? '能力覆盖自评：' : 'Capability self-assessment: ') +
          capabilityAssessments.map((row) =>
            labels[row.capability] + ' — ' + assessmentLabels[row.state]
          ).join(' / ')
        );

        if (notes.value.trim()) {
          additions.push(
            (zh ? '资料及问题：' : 'Material and questions: ') +
            notes.value.trim()
          );
        }

        const description = [base, additions.join('\n')]
          .filter(Boolean)
          .join('\n\n');

        if (description.length > 1200) {
          status.textContent = zh
            ? '合并资料超过需求栏位的1200字符上限，请缩短补充内容。原招标资料未截断。'
            : 'Combined details exceed 1,200 characters. Shorten your notes; tender context was not truncated.';
          return;
        }

        window.sessionStorage.setItem(key, JSON.stringify({
          ...(stored || {}),
          version: 1,
          source,
          created_at: Date.now(),
          project_location: location.value.trim(),
          description,
          low_altitude_base_description: base,
          capability_assessments: capabilityAssessments,
          capability_gaps: capabilityAssessments
            .filter((row) => row.state === 'partner')
            .map((row) => ({
              capability: row.capability,
              status: 'partner_requested',
              basis: 'user_self_report',
              verified: false
            })),
          partner_requests: capabilityAssessments
            .filter((row) =>
              row.state === 'partner' || row.state === 'self_partner'
            )
            .map((row) => ({
              capability: row.capability,
              reason: row.state === 'partner'
                ? 'support_needed'
                : 'voluntary_collaboration',
              scope_status: 'not_confirmed',
              basis: 'user_self_report',
              verified: false
            })),
          workload_estimate: readWorkloadEstimate(panel),
          low_altitude_preparation: {
            services,
            notes: notes.value.trim()
          }
        }));

        destination.searchParams.set('source', source);
        destination.searchParams.delete('dashboard_handoff');
        window.location.assign(destination.href);
      } catch (_) {
        status.textContent = zh
          ? '无法安全交接：暂存不可用、资料已过期，或标签页有其他待处理需求。未覆盖原资料，也未跳转。'
          : 'Handoff could not be completed safely. Storage, expiry or another pending request needs attention. Nothing was overwritten and navigation was stopped.';
      }
   });

    button.disabled = false;

    const capabilityCount = panel.querySelector(
      '[data-la-capability-count]'
    );

    function updateCapabilityCount() {
      if (!capabilityCount) return;

      const selectedServices = Array.from(
        panel.querySelectorAll('[data-la-service]:checked'),
        (input) => input.value
      ).filter((value) =>
        Object.prototype.hasOwnProperty.call(labels, value)
      );
      const rows = readCapabilityAssessment(panel, selectedServices);
      const pending = rows.filter(
        (row) => row.state === 'unknown'
      ).length;
      const partners = rows.filter(
        (row) => row.state === 'partner' || row.state === 'self_partner'
      ).length;

      capabilityCount.textContent = zh
        ? `已选${rows.length}项服务 · ${pending}项还未确定 · ${partners}项希望合作`
        : `${rows.length} services selected · ${pending} undecided · ${partners} seeking collaboration`;
    }

    panel.querySelectorAll(
      '[data-la-service], [data-la-capability]'
    ).forEach((input) => {
      input.addEventListener('change', updateCapabilityCount);
    });
    updateCapabilityCount();

    root.querySelectorAll(
      '.la-services [data-la-prepare-service]'
    ).forEach((link) => {
      link.addEventListener('click', (event) => {
        const service = link.getAttribute('data-la-prepare-service');
        const input = Array.from(
          panel.querySelectorAll('[data-la-service]')
        ).find((node) => node.value === service);

        if (!input) return;
        event.preventDefault();

        if (input.disabled || location.readOnly) {
          status.textContent = zh
            ? '已有保存的专业需求草稿。本页不会更改服务选择，请使用继续编辑按钮进入草稿。'
            : 'A professional brief has been saved. Services were not changed; use the continue button to edit the draft.';
        } else {
          input.checked = true;
          input.dispatchEvent(new window.Event('change', { bubbles: true }));
          status.textContent = zh
            ? '已加入服务选择，可继续补充项目区域和资料。'
            : 'Service selected. Add your project area and material details.';
        }

        let reduceMotion = false;
        try {
          reduceMotion = window.matchMedia(
            '(prefers-reduced-motion: reduce)'
          ).matches;
        } catch (_) {}

        panel.setAttribute('tabindex', '-1');
        try { panel.focus({ preventScroll: true }); } catch (_) {}
        try {
          panel.scrollIntoView({
            behavior: reduceMotion ? 'auto' : 'smooth',
            block: 'start'
          });
        } catch (_) {}
      });
    });

    window.addEventListener('pageshow', (event) => {
      if (!event.persisted || !panel.isConnected) return;

      try {
        const value = readHandoff();

        if (value && !accepts(value)) {
          status.textContent = zh
            ? '标签页的待处理需求已改变。本页输入未覆盖，请核对原需求后再继续。'
            : 'The pending request in this tab has changed. Your inputs were not overwritten; check the original request before continuing.';
          return;
        }

        if (!value && incomingSource === 'gtender') {
          status.textContent = zh
            ? '原招标交接资料已不可用。请从该招标详情重新进入；当前输入未清除。'
            : 'Tender handoff details are no longer available. Enter again from the tender detail; current inputs were not cleared.';
          return;
        }

        if (value && value.brief_record_id) {
          status.textContent = zh
            ? '专业需求已有保存草稿。继续时会核对最新记录；本页未自动刷新或覆盖输入。'
            : 'A professional draft has been saved. Continuing checks the latest record; this page was not refreshed and your inputs were not overwritten.';
        }
      } catch (_) {
        status.textContent = zh
          ? '返回后发现暂存资料不可用或已过期。当前输入仍保留，请勿把它当作已保存资料。'
          : 'Temporary details are unavailable or expired after returning. Current inputs remain, but they should not be treated as saved.';
      }
    });
  }

  function mount(root) {
    if (!root || root.dataset.laMounted === 'true') return;
    root.dataset.laMounted = 'true';

    mountProjectPreparation(root);
    mountWorkloadEstimator(root);

    var form = root.querySelector('[data-la-diagnosis-form]');
    if (!form) return;
    var steps = all(form, '[data-la-step]');
    var current = 0;
    var progress = root.querySelector('[data-la-progress]');
    var progressText = root.querySelector('[data-la-progress-text]');
    var previous = root.querySelector('[data-la-prev]');
    var next = root.querySelector('[data-la-next]');
    var submit = root.querySelector('[data-la-submit]');

    function showStep(index, options) {
      current = Math.max(0, Math.min(steps.length - 1, index));
      steps.forEach(function (step, stepIndex) {
        step.hidden = stepIndex !== current;
        step.setAttribute('aria-hidden', stepIndex === current ? 'false' : 'true');
      });
      var percent = Math.round(((current + 1) / steps.length) * 100);
      if (progress) progress.style.width = percent + '%';
      if (progressText) progressText.textContent = String(current + 1) + ' / ' + String(steps.length);
      if (previous) previous.hidden = current === 0;
      if (next) next.hidden = current === steps.length - 1;
      if (submit) submit.hidden = current !== steps.length - 1;

      const legend = steps[current] &&
        steps[current].querySelector('legend');
      const disclosure = form.closest('details.la-tools');

      if (
        legend &&
        (!options || options.quiet !== true) &&
        (!disclosure || disclosure.open)
      ) {
        legend.setAttribute('tabindex', '-1');
        try { legend.focus({ preventScroll: true }); } catch (_) {}
      }
    }

    if (previous) previous.addEventListener('click', function () { showStep(current - 1); });
    if (next) {
      next.addEventListener('click', function () {
        if (!validateStep(steps[current])) return;
        showStep(current + 1);
      });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (!validateStep(steps[current])) return;
      var answers = collectAnswers(form);
      var result = buildResult(answers, root.dataset.locale || 'zh-Hant');
      const resultStored = safeStore(result);
      renderResult(root, result);

      if (!resultStored) {
        const panel = root.querySelector('[data-la-result]');
        const summary = panel &&
          panel.querySelector('[data-la-diagnosis-summary]');
        if (summary) {
          summary.textContent += isChinese(root.dataset.locale)
            ? ' 本次结果仅在当前页面显示，浏览器未能保存；刷新或离开后可能丢失。'
            : ' This result is displayed on this page only. Browser storage failed; it may be lost after refresh or navigation.';
        }
      }
    });

    var restart = root.querySelector('[data-la-restart]');
    if (restart) {
      restart.addEventListener('click', function () {
        form.reset();
        try { window.localStorage.removeItem(STORAGE_KEY); } catch (_) {}
        var panel = root.querySelector('[data-la-result]');
        if (panel) panel.hidden = true;
        root.dataset.laStatus = 'idle';
        showStep(0);
        try { form.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (_) {}
      });
    }

    var stored = readStored();
    if (stored && stored.schema_version === 'low_altitude_diagnosis_v2') {
      renderResult(root, stored, { quiet: true });
    }
    showStep(0, { quiet: true });
  }

  function mountAll() {
    if (!window.document) return;
    all(window.document, '[data-low-altitude-diagnosis]').forEach(mount);
  }

  if (window.document) {
    if (window.document.readyState === 'loading') {
      window.document.addEventListener('DOMContentLoaded', mountAll, { once: true });
    } else {
      mountAll();
    }
    window.document.addEventListener('shopify:section:load', mountAll);
  }

  return {
    version: VERSION,
    scenarios: SCENARIOS,
    weights: WEIGHTS,
    scoreAnswers: scoreAnswers,
    calculateMaturity: calculateMaturity,
    matchProfile: matchProfile,
    buildResult: buildResult,
    mount: mount,
    mountAll: mountAll
  };
});
